import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FOCUS_KPI_DEFS, FOCUS_SLOTS, SUB_KGI_KEYS, focusLeverKeysFor } from '../../utils/kpiFocusCatalog';
import { fmtWhen } from './ApprovalTable';

const UNIT_SUFFIX = { percent: '%', currency: '円', count: '名', pieces: '件', days: '日' };

/** 「指名率 68%」 — same wording as the server's fmt_kpi, for the live edit preview. */
function fmtKpi(key, target) {
  if (!key) return '未設定';
  const d = FOCUS_KPI_DEFS[key];
  if (!d) return key;
  if (target == null || target === '') return d.label;
  const n = Number(target);
  if (d.unit === 'currency') return `${d.label} ¥${Math.round(n).toLocaleString('ja-JP')}`;
  return `${d.label} ${n}${UNIT_SUFFIX[d.unit] || ''}`;
}

const yen = (v) => (v == null || v === '' ? '未設定' : `¥${Math.round(Number(v)).toLocaleString('ja-JP')}`);

/** Only the keys whose value differs from what was submitted go to the server. */
function diffPatch(after, draft) {
  const out = {};
  const same = (a, b) => (a == null || a === '' ? b == null || b === '' : Number(a) === Number(b) || a === b);
  Object.keys(draft).forEach((k) => {
    if (!same(after?.[k], draft[k])) out[k] = draft[k] === '' ? null : draft[k];
  });
  return out;
}

/**
 * Bottom sheet for one 目標 submission (type=goals).
 * - 確認中 + canEdit: before → after rows; 売上ゴール / 追加KGI / 注力①②③ editable, optional comment, 確定.
 *   The values are already live (案 B) — 確定 is the manager's review; corrections are written back.
 * - 確定済み: read-only, shows what the manager corrected and the comment.
 */
export default function GoalApprovalSheet({ row, isPending, canEdit, submitting, onClose, onApprove }) {
  const edit = canEdit && isPending;
  const after = useMemo(() => row.after || {}, [row.after]);
  const [draft, setDraft] = useState(() => ({
    finalSales: after.finalSales ?? '',
    subKgiKey: after.subKgiKey ?? '',
    subKgiTarget: after.subKgiTarget ?? '',
    focusKey: after.focusKey ?? '',
    focusTarget: after.focusTarget ?? '',
    subFocusKey: after.subFocusKey ?? '',
    subFocusTarget: after.subFocusTarget ?? '',
    thirdFocusKey: after.thirdFocusKey ?? '',
    thirdFocusTarget: after.thirdFocusTarget ?? '',
  }));
  const [note, setNote] = useState('');
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const levers = useMemo(() => focusLeverKeysFor({ subKgiKey: draft.subKgiKey || null }), [draft.subKgiKey]);
  const patch = useMemo(() => diffPatch(after, draft), [after, draft]);
  const edited = Object.keys(patch).length > 0;
  // Same rule as the server (validate_goals_patch): ①②③ all different, none equal to a KGI.
  const chosen = [draft.focusKey, draft.subFocusKey, draft.thirdFocusKey].filter(Boolean);
  const conflict =
    new Set(chosen).size !== chosen.length || chosen.some((k) => !levers.includes(k)) ||
    (draft.subKgiKey && !(Number(draft.subKgiTarget) > 0));
  const canSubmit = edit && !submitting && !conflict && Number(draft.finalSales) > 0;

  const host = typeof document !== 'undefined' ? document.querySelector('.kuroco-shell') || document.body : null;
  const changes = row.changes || [];
  const managerChanges = row.managerChanges || [];

  const slotRow = (s) => {
    const key = draft[s.keyField];
    const d = key ? FOCUS_KPI_DEFS[key] : null;
    return (
      <div className="apv-goal-field" key={s.slot}>
        <div className="apv-fl">注力指標{s.label}</div>
        <div className="apv-goal-inputs">
          <select
            className="apv-select"
            value={key}
            onChange={(e) => set(s.keyField, e.target.value)}
            aria-label={`注力指標${s.label}の指標`}
          >
            <option value="">{s.slot === 1 ? '選択' : 'なし'}</option>
            {levers.map((k) => (
              <option key={k} value={k}>{FOCUS_KPI_DEFS[k].label}</option>
            ))}
          </select>
          {key && (
            <input
              className="apv-num"
              type="number"
              inputMode="decimal"
              value={draft[s.targetField]}
              onChange={(e) => set(s.targetField, e.target.value)}
              aria-label={`注力指標${s.label}の目標値`}
            />
          )}
          {d && <span className="apv-unit">{UNIT_SUFFIX[d.unit]}</span>}
        </div>
      </div>
    );
  };

  const sheet = (
    <div className="apv-mask" onClick={onClose} role="presentation">
      <div className="apv-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="apv-grab" />
        <div className="apv-sheet-h">
          <div>
            <h2>{row.stylist} <span className="apv-kind apv-kind--goals">目標</span></h2>
            <div className="apv-meta">
              {row.store} · {row.initialSetup ? '初期設定' : '変更'} {fmtWhen(row.submittedAt)}
              {row.reviewedAt ? ` · 確定 ${fmtWhen(row.reviewedAt)}` : ''}
              {row.reviewedByName ? `（${row.reviewedByName}）` : ''}
            </div>
          </div>
          <button type="button" className="apv-x" onClick={onClose} aria-label="閉じる">×</button>
        </div>

        <div className="apv-block">
          <div className="apv-lbl">{row.initialSetup ? '設定した内容' : '変更した内容（確定済み → 今の値）'}</div>
          {edit && <p className="apv-cap">値はもうホームに反映されています。直すと本人の目標が書き換わり、本人に通知されます。</p>}
          {changes.length === 0 && <p className="apv-cap">変更はありません</p>}
          {changes.map((c) => (
            <div className="apv-diff" key={c.field}>
              <span className="apv-diff-f">{c.field}</span>
              <span className="apv-diff-b">{c.before || '未設定'}</span>
              <span className="apv-diff-arrow">→</span>
              <span className="apv-diff-a">{c.after || '未設定'}</span>
            </div>
          ))}
        </div>

        {edit ? (
          <div className="apv-block">
            <div className="apv-lbl">直してよい</div>
            <div className="apv-goal-field">
              <div className="apv-fl">売上ゴール（単月）</div>
              <div className="apv-goal-inputs">
                <input
                  className="apv-num"
                  type="number"
                  inputMode="numeric"
                  step={10000}
                  value={draft.finalSales}
                  onChange={(e) => set('finalSales', e.target.value)}
                  aria-label="売上ゴール"
                />
                <span className="apv-unit">円</span>
              </div>
            </div>
            <div className="apv-goal-field">
              <div className="apv-fl">追加KGI</div>
              <div className="apv-goal-inputs">
                <select className="apv-select" value={draft.subKgiKey} onChange={(e) => set('subKgiKey', e.target.value)} aria-label="追加KGIの指標">
                  <option value="">なし</option>
                  {SUB_KGI_KEYS.map((k) => (
                    <option key={k} value={k}>{FOCUS_KPI_DEFS[k].label}</option>
                  ))}
                </select>
                {draft.subKgiKey && (
                  <>
                    <input className="apv-num" type="number" inputMode="decimal" value={draft.subKgiTarget} onChange={(e) => set('subKgiTarget', e.target.value)} aria-label="追加KGIの目標値" />
                    <span className="apv-unit">{UNIT_SUFFIX[FOCUS_KPI_DEFS[draft.subKgiKey]?.unit]}</span>
                  </>
                )}
              </div>
            </div>
            {FOCUS_SLOTS.map(slotRow)}
            {conflict && <p className="apv-cap apv-cap--warn">KGI と注力、注力どうしは別の指標にしてください。追加KGI の目標値は 0 より大きく。</p>}
            {edited && !conflict && (
              <p className="apv-cap">
                直した後: 売上 {yen(draft.finalSales)}
                {draft.subKgiKey ? ` · 追加KGI ${fmtKpi(draft.subKgiKey, draft.subKgiTarget)}` : ''}
                {FOCUS_SLOTS.filter((s) => draft[s.keyField]).map((s) => ` · ${s.label}${fmtKpi(draft[s.keyField], draft[s.targetField])}`).join('')}
              </p>
            )}
          </div>
        ) : (
          managerChanges.length > 0 && (
            <div className="apv-block">
              <div className="apv-lbl">マネージャーが直した項目</div>
              {managerChanges.map((c) => (
                <div className="apv-diff" key={c.field}>
                  <span className="apv-diff-f">{c.field}</span>
                  <span className="apv-diff-b">{c.before || '未設定'}</span>
                  <span className="apv-diff-arrow">→</span>
                  <span className="apv-diff-a">{c.after || '未設定'}</span>
                </div>
              ))}
            </div>
          )
        )}

        {edit ? (
          <div className="apv-block apv-footer">
            <div className="apv-lbl">マネージャーコメント（スタイリストへ）</div>
            <p className="apv-cap">任意。書けばスタイリストの「コメント」タブとAI相談へ渡る。確定は常に本人へ通知されます。</p>
            <textarea
              className="apv-ta"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例: 売上は一旦この額で。指名率を優先で見ていこう。"
              maxLength={1000}
            />
            <button type="button" className="apv-btn" disabled={!canSubmit} onClick={() => onApprove(patch, note.trim())}>
              {submitting ? '確定中…' : edited ? '修正して確定' : '確定'}
            </button>
            <p className="apv-hint">確定すると行は行動記録管理へ移ります。</p>
          </div>
        ) : (
          <div className="apv-block apv-footer">
            <div className="apv-lbl">マネージャーコメント</div>
            {row.managerNote ? <div className="apv-note">{row.managerNote}</div> : <p className="apv-cap">コメントなし</p>}
            {isPending && !canEdit && <p className="apv-hint">目標の確定はマネージャーが行います。ここでは内容の確認だけできます。</p>}
          </div>
        )}
      </div>
    </div>
  );
  return host ? createPortal(sheet, host) : sheet;
}
